import React from 'react';
import { createBrowserRouter, Navigate, RouteObject, Outlet, useRouteError, useLocation } from 'react-router-dom';
import { ShellLayout } from './layout/ShellLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { HomePage } from './pages/HomePage';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError() as any;
  const errorStr = (error?.message || '') + (error?.stack || '') + String(error);

  if (
    errorStr.includes('Failed to fetch dynamically imported module') ||
    errorStr.includes('Expected a JavaScript-or-Wasm module script') ||
    errorStr.includes('Loading chunk') ||
    errorStr.includes('Failed to load module script')
  ) {
    const reloadKey = `chunk_reload_${window.location.pathname}`;
    const reloadAttempts = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);

    // Only reload ONCE per route session. If it fails again, it is an ad-blocker or permanent network failure.
    if (reloadAttempts < 1) {
      sessionStorage.setItem(reloadKey, String(reloadAttempts + 1));
      window.location.reload();
      return (
        <div className="flex h-screen items-center justify-center bg-background text-foreground p-6 text-center">
          <div className="space-y-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <h2 className="text-base font-bold">Refreshing application assets...</h2>
            <p className="text-xs text-muted-foreground">Synchronizing with latest build.</p>
          </div>
        </div>
      );
    }

    // Suppress reload loop; display content shield advisory
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground p-6 text-center">
        <div className="max-w-md p-6 rounded-2xl bg-card border border-border shadow-md space-y-4">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-base font-bold text-foreground">Browser Shield or Network Notice</h2>
          <p className="text-xs text-muted-foreground">
            A script required to display this view could not be loaded. This typically occurs when a browser content blocker (such as Brave Shields or uBlock Origin) intercepts internal application assets.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => {
                sessionStorage.removeItem(reloadKey);
                window.location.reload();
              }}
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-bold transition cursor-pointer"
            >
              Retry
            </button>
            <button
              onClick={() => {
                window.location.href = '/core/dashboard';
              }}
              className="h-9 px-4 rounded-lg bg-secondary text-secondary-foreground text-xs font-medium transition cursor-pointer"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-background text-foreground p-6 text-center">
      <div className="max-w-md p-6 rounded-2xl bg-card border border-border shadow-md space-y-4">
        <h2 className="text-lg font-bold text-rose-500">Navigation Notice</h2>
        <p className="text-xs text-muted-foreground">{error?.message || 'An error occurred while loading this view.'}</p>
        <button
          onClick={() => window.location.reload()}
          className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-bold transition cursor-pointer"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
};

const SuiteGuard: React.FC<{ suiteId: string; requiredPerm?: string; allowedRoles?: string[] }> = ({
  suiteId,
  requiredPerm,
  allowedRoles,
}) => {
  const { role, hasPermission, hasSuite } = useAuth();
  if (!hasSuite(suiteId)) {
    return <ForbiddenPage />;
  }
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    return <ForbiddenPage />;
  }
  if (requiredPerm && !hasPermission(requiredPerm)) {
    return <ForbiddenPage />;
  }
  return <Outlet />;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated, role, hasSuite } = useAuth();

  // If unauthenticated, redirect to public landing page
  if (!isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  // Role-based smart entry suite
  if ((role === 'teacher' || role === 'student' || role === 'parent') && hasSuite('lms')) {
    return <Navigate to="/lms/live" replace />;
  }
  if ((role === 'student' || role === 'parent') && hasSuite('sis')) {
    return <Navigate to="/sis/dashboard" replace />;
  }
  if (role === 'counselor' && hasSuite('crm')) {
    return <Navigate to="/crm/leads" replace />;
  }
  if (role === 'accountant' && hasSuite('fees')) {
    return <Navigate to="/fees/invoices" replace />;
  }
  if (hasSuite('bi') && (role === 'owner' || role === 'admin' || role === 'branch_head')) {
    return <Navigate to="/bi/executive" replace />;
  }

  // Universal baseline entry for Basic Subscription
  return <Navigate to="/core/dashboard" replace />;
};

const AuthenticatedShellLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background text-foreground">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground mt-4">Connecting to Helios Cloud...</p>
      </div>
    );
  }

  // When an unauthenticated visitor accesses root `/`, redirect to `/home`
  if (!isAuthenticated && location.pathname === '/') {
    return <Navigate to="/home" replace />;
  }

  return <ShellLayout />;
};

export function createAppRouter() {
  const suiteRouteObjects: RouteObject[] = allSuites.map((suite) => ({
    path: suite.basePath.replace(/^\//, ''),
    element: (
      <SuiteGuard
        suiteId={suite.id}
        requiredPerm={suite.requiredPermission}
        allowedRoles={suite.allowedRoles}
      />
    ),
    errorElement: <RouteErrorBoundary />,
    children: suite.routes,
  }));

  return createBrowserRouter([
    {
      path: '/home',
      element: <HomePage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/auth/login',
      element: <LoginPage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/login',
      element: <LoginPage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/auth/register',
      element: <RegisterPage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/register',
      element: <RegisterPage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/403',
      element: <ForbiddenPage />,
    },
    {
      path: '/404',
      element: <NotFoundPage />,
    },
    {
      path: '/',
      element: <AuthenticatedShellLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          index: true,
          element: <RootRedirect />,
        },
        ...suiteRouteObjects,
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ]);
}
